import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface WhatsAppConfigResponse {
  configured: boolean;
  phoneNumber: string | null;
  phoneNumberId: string | null;
  wabaId: string | null;
  status: string | null;
  tokenConfigured: boolean;
  webhookSecretConfigured: boolean;
  messagingEnabled: boolean;
  updatedAt: string | null;
  lastInboundAt: string | null;
}

export interface WhatsAppAdminTemplate {
  id: string;
  name: string;
  category: string;
  language: string;
  headerText: string | null;
  body: string;
  footer: string | null;
  parameterCount: number;
  status?: 'draft' | 'pending' | 'approved' | 'rejected';
  rejectionReason?: string | null;
}

export interface SaveWhatsAppConfigInput {
  phoneNumber: string;
  phoneNumberId: string;
  wabaId: string;
  status: 'active' | 'pending';
  accessToken?: string;
  webhookVerifyToken?: string;
  messagingEnabled: boolean;
}

export function useWhatsAppConfig(enabled: boolean) {
  return useQuery({
    queryKey: ['whatsapp', 'config'],
    queryFn: () => api.get<WhatsAppConfigResponse>('/whatsapp/config'),
    enabled,
  });
}

export interface WhatsAppStatusResponse {
  enabled: boolean;
  reason: 'not_configured' | 'inactive' | 'token_missing' | null;
}

/**
 * Whether this workshop can send WhatsApp messages (#3124). Any signed-in user
 * may ask; send dialogs use it to disable the channel up front instead of
 * failing on send. The answer only changes when settings do.
 */
export function useWhatsAppStatus(enabled = true) {
  return useQuery({
    queryKey: ['whatsapp', 'status'],
    queryFn: () => api.get<WhatsAppStatusResponse>('/whatsapp/status'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useWhatsAppAdminTemplates(enabled: boolean) {
  return useQuery({
    queryKey: ['whatsapp', 'templates', 'all'],
    queryFn: async () => {
      const response = await api.get<{ data: WhatsAppAdminTemplate[] }>('/whatsapp/templates?all=1');
      return response.data;
    },
    enabled,
  });
}

export function useSaveWhatsAppConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SaveWhatsAppConfigInput) => api.put('/whatsapp/config', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['whatsapp', 'config'] });
    },
  });
}

export function useTestWhatsAppConfig() {
  return useMutation({
    mutationFn: (to: string) =>
      api.post<{ success: boolean; error?: string; waMessageId?: string }>('/whatsapp/config/test', { to }),
  });
}

export function useSyncWhatsAppTemplates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ success: boolean; error?: string; synced?: number; data?: WhatsAppAdminTemplate[] }>(
        '/whatsapp/templates/sync',
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['whatsapp', 'templates'] });
    },
  });
}
